import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";
import { ChatAppShell } from "@trillion/ui/components/chat/chat-app-shell";
import type { ChatShellLinkComponent } from "@trillion/ui/components/chat/chat-conversation-list";
import { getEnv } from "@trillion/ui/lib/env";
import { DefaultGreeting } from "@/components/brand/default-greeting";
import { HelpLogo, HelpLogoMark } from "@/components/brand/help-logo";
import { ChatContainer } from "@/components/chat/chat-container";
import { useChatStream, type ChatContext } from "@/hooks/use-chat-stream";
import { helpApi } from "@/lib/help-api";
import { CONVERSATION_ROUTE } from "@/lib/constants";
import {
  browserStorage,
  createConversation,
  deleteConversation,
  deriveTitle,
  listConversations,
  loadConversation,
  migrateLegacyConversation,
  newConversationId,
  saveConversation,
  type ConversationMeta,
  type TrafficContext,
} from "@/lib/conversation-store";

interface TrafficIntent {
  topic: string;
  category?: string;
  buying_stage: string;
  greeting?: string;
  suggestion_chips?: string[] | null;
  source_type: string;
  source_key: string;
  channel?: string;
  confidence: number;
}

/**
 * Adapter over React Router's Link for @trillion/ui's router-agnostic LinkComponent contract.
 * Spreading every prop (ref included — React 19 passes it as an ordinary prop) is what gives link
 * rows the collapsed rail's Radix tooltip, which drives through a ref on the rendered element.
 */
function ChatLink(props: React.ComponentProps<typeof Link>) {
  return <Link {...props} />;
}

/**
 * The persistent chat layout, rendered once above both `/` and `/c/:conversationId`.
 *
 * It owns everything the route hop must not disturb: the landing traffic context, the single
 * session, the intent fetch, the conversation store, `useChatStream` and which conversation is
 * active. The two routes below it are param-only — they render nothing of their own — so moving
 * between them never remounts the hook and never restarts a stream.
 */
export function ChatLayout() {
  const apiUrl = getEnv("HELP_API_URL");
  const navigate = useNavigate();
  const location = useLocation();

  // A layout route cannot see a descendant's params through useParams() — each match's params
  // accumulate from ancestors only — so the pattern is matched explicitly here.
  const paramId = useMatch(CONVERSATION_ROUTE)?.params.conversationId ?? null;

  const [input, setInput] = useState("");
  const [trafficIntent, setTrafficIntent] = useState<TrafficIntent | null>(null);
  const [conversations, setConversations] = useState<ConversationMeta[]>([]);

  const { messages, isStreaming, sendMessage, stop, setMessages } = useChatStream(apiUrl);

  const storage = useMemo(() => browserStorage(), []);

  /** The conversation the send path and the persist effect belong to. */
  const activeIdRef = useRef<string | null>(null);
  /** The traffic context that conversation was started with — what its stream sends. */
  const activeContextRef = useRef<TrafficContext>({});
  /** This landing's captured context, kept so a New chat still carries it. */
  const landingContextRef = useRef<TrafficContext>({});
  const sessionIdRef = useRef<string | null>(null);
  const landingRanRef = useRef(false);

  // Mirrors location.search so every navigation helper can preserve it without becoming an
  // effect dependency (which would re-run the param-sync effect on an unrelated query change).
  const searchRef = useRef(location.search);
  searchRef.current = location.search;

  const refreshList = useCallback(() => {
    setConversations(storage ? listConversations(storage) : []);
  }, [storage]);

  // Mirrors `conversations` so the per-token persist effect can read the current order without
  // taking it as a dependency (which would re-run the effect on its own refresh).
  const conversationsRef = useRef<ConversationMeta[]>([]);
  conversationsRef.current = conversations;

  /**
   * The single writer of activeIdRef / activeContextRef (the only other writer is
   * startOrContinue's first-send mint). It is where the store drives setMessages for every
   * conversation that has an envelope; the one other store-driven call is the landing's
   * unpersistable-legacy recovery, which deliberately opens a transcript that has no envelope.
   *
   * Taking the refs over is load-bearing: without it a queryless reload of `/c/<id>` renders the
   * transcript but leaves activeIdRef null — silently skipping the persist effect — and leaves
   * activeContextRef holding this landing's empty query, so the next POST /api/chat/stream drops
   * the stored source_key / utm_* / referrer.
   *
   * Returns false when `id` names a conversation the store does not hold.
   */
  const openConversation = useCallback(
    (id: string | null): boolean => {
      stop();
      setInput("");

      if (id === null) {
        activeIdRef.current = null;
        // Restore the landing context so a following first send still carries it.
        activeContextRef.current = landingContextRef.current;
        setMessages([]);
        return true;
      }

      const envelope = storage ? loadConversation(storage, id) : null;
      if (!envelope) return false;

      activeIdRef.current = id;
      activeContextRef.current = envelope.traffic_context;
      setMessages(envelope.messages);
      return true;
    },
    [setMessages, stop, storage]
  );

  // Landing: capture the traffic context, fetch intent, create the session, migrate the legacy
  // thread. Runs exactly once per mounted instance and never touches activeIdRef. It touches
  // messages in exactly one case — a legacy thread that was readable but unpersistable, opened in
  // memory below — and never claims an id for it.
  useEffect(() => {
    // The ref guard is required, not defensive: src/main.tsx mounts the app under <StrictMode>
    // and React 19's development build double-invokes mount effects, so an unguarded effect fires
    // two POST /api/session calls on every dev landing. A ref survives StrictMode's simulated
    // unmount/remount, so the second invocation no-ops. Instance-scoped on purpose — a module
    // global would leak across the test suites' repeated mounts.
    if (landingRanRef.current) return;
    landingRanRef.current = true;

    const params = new URLSearchParams(searchRef.current);
    const ctx: TrafficContext = {
      // Canonical only — ?source_key= is the sole source_key input (sc-98387 removed the
      // legacy ?src=/?domain= aliases; a landing without it sends no source_key at all).
      source_key: params.get("source_key") ?? undefined,
      source_partner: params.get("source_partner") || undefined,
      referrer: document.referrer || undefined,
      utm_source: params.get("utm_source") || undefined,
      utm_campaign: params.get("utm_campaign") || undefined,
    };
    // This landing's initialization of both refs — not a load. It is the only thing that puts a
    // context in activeContextRef before the first send.
    landingContextRef.current = ctx;
    activeContextRef.current = ctx;

    // Fetch traffic intent from C# Help backend (source_key only — partner-aware intent is future work)
    if (ctx.source_key || ctx.referrer || ctx.utm_source) {
      const intentParams = new URLSearchParams();
      if (ctx.source_key) intentParams.set("source_key", ctx.source_key);
      if (ctx.referrer) intentParams.set("referrer", ctx.referrer);
      if (ctx.utm_source) intentParams.set("utm_source", ctx.utm_source);
      if (ctx.utm_campaign) intentParams.set("utm_campaign", ctx.utm_campaign);

      fetch(`${apiUrl}/api/chat/intent?${intentParams.toString()}`)
        .then((res) => res.json())
        .then((intent) => setTrafficIntent(intent))
        .catch(() => {}); // Continue without traffic context
    }

    // Create a session for message persistence. A ref, not state: toChatContext() reads it only
    // at send time and nothing renders it, so state would buy a re-render and a stale-closure
    // hazard on the send path.
    helpApi
      .createSession({
        source_key: ctx.source_key,
        source_partner: ctx.source_partner,
        referrer: ctx.referrer,
        utm_source: ctx.utm_source,
        utm_campaign: ctx.utm_campaign,
      })
      .then(({ session_id }) => {
        sessionIdRef.current = session_id;
      })
      .catch(() => {}); // Continue without session

    if (storage) {
      const migration = migrateLegacyConversation(storage, ctx);
      refreshList();
      if (migration.migrated) {
        navigate(`/c/${migration.id}${searchRef.current}`, { replace: true });
      } else if (migration.recovered) {
        // The legacy thread was readable but could not be persisted. Open it in memory for this
        // session rather than showing an empty greeting over a transcript the store still holds:
        // AC 3 forbids losing messages, and the storage-failure decision says the chat continues
        // in-memory on a failed write. activeIdRef stays null, so this conversation has no envelope
        // to save into and the legacy key is left intact for a later retry. Safe against the param
        // sync below: on `/` paramId is null and activeIdRef is null, so that effect returns early
        // and never calls setMessages([]) over this.
        setMessages(migration.recovered);
      }
    }
  }, [apiUrl, navigate, refreshList, setMessages, storage]);

  // Param sync. The dependency list is exactly [paramId] and nothing else: this effect reacts to
  // the URL's conversation id changing, and only to that. Anything closing over `messages` would
  // re-run it mid-stream and reload the transcript over the live one.
  useEffect(() => {
    // The first-send hop, and New chat / delete, which have already called openConversation.
    // The post-migration landing is NOT skipped here — the landing effect never claims the id, so
    // activeIdRef is still null and this load is what puts the restored thread on screen.
    if (paramId === activeIdRef.current) return;

    if (!openConversation(paramId)) {
      navigate(`/${searchRef.current}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramId]);

  // Persist the open conversation on every message change — the same per-token cadence the app
  // has always had, now scoped to one conversation's envelope.
  useEffect(() => {
    const id = activeIdRef.current;
    if (!storage || !id || messages.length === 0) return;
    const saved = saveConversation(storage, id, {
      messages,
      traffic_context: activeContextRef.current,
    });
    // A successful save bumps this conversation's updated_at, so the sidebar's
    // most-recently-updated-first order goes stale the moment an older conversation is continued —
    // its row has to move to the top without waiting for a reload or an unrelated create/delete.
    // Guarded on "not already on top" because this effect runs once per streamed token: an
    // unconditional refresh would re-parse the index and re-render the sidebar on every frame.
    if (saved.ok && conversationsRef.current[0]?.id !== id) refreshList();
  }, [messages, storage, refreshList]);

  /**
   * The only place a stream context is built, and the boundary where the two casings meet:
   * useChatStream's ChatContext is camelCase and the stored envelope's traffic_context is
   * snake_case, so they are mapped field by field. Never spread a TrafficContext into a
   * ChatContext — every ChatContext field is optional, so the spread type-checks and silently
   * sends all five fields as undefined. Never read paramId or location.search here either:
   * paramId is still null on the first send from `/`, and the search is empty on a queryless
   * reload of `/c/<id>`.
   */
  const toChatContext = useCallback((): ChatContext => {
    const ctx = activeContextRef.current;
    return {
      sessionId: sessionIdRef.current ?? undefined,
      conversationId: activeIdRef.current ?? undefined,
      sourceKey: ctx.source_key,
      sourcePartner: ctx.source_partner,
      referrer: ctx.referrer,
      utmSource: ctx.utm_source,
      utmCampaign: ctx.utm_campaign,
    };
  }, []);

  /** Both the composer and the suggestion chips send through here. */
  const startOrContinue = useCallback(
    (text: string) => {
      // Continue: a follow-up send in an already-open conversation, whether it was opened by the
      // first-send hop or by a store load. No mint, no create, no navigate.
      if (activeIdRef.current) {
        void sendMessage(text, toChatContext());
        return;
      }

      // First send from `/`: write the conversation before anything else, so param-sync can never
      // meet the id it is about to see as an unknown one.
      const id = newConversationId();
      const landingContext = landingContextRef.current;
      if (storage) {
        createConversation(storage, {
          id,
          title: deriveTitle(text),
          messages: [{ id: crypto.randomUUID(), role: "user", content: text, toolCalls: [] }],
          traffic_context: landingContext,
        });
      }

      // Both refs are set even when that write failed, so the conversation stays usable in memory
      // and param-sync cannot later treat its id as unknown. activeContextRef is not redundant
      // with the landing effect's copy: the persist effect writes activeContextRef.current on
      // every message change, so a ref left empty would overwrite the context just stored, and a
      // later queryless /c/<id> load would then send nothing.
      activeIdRef.current = id;
      activeContextRef.current = landingContext;
      refreshList();

      // Not awaited: the hop must not wait for the stream, and the stream must survive it.
      void sendMessage(text, toChatContext());
      navigate(`/c/${id}${searchRef.current}`, { replace: true });
    },
    [navigate, refreshList, sendMessage, storage, toChatContext]
  );

  const handleSend = useCallback(() => {
    if (!input.trim()) return;
    const text = input;
    setInput("");
    startOrContinue(text);
  }, [input, startOrContinue]);

  const handleNewChat = useCallback(() => {
    // Clear first, navigate second, so the param-sync effect finds nothing to do.
    openConversation(null);
    navigate(`/${searchRef.current}`, { replace: true });
  }, [navigate, openConversation]);

  /**
   * Decide against `id`, not against "current". ChatConversationList forwards the pending row's
   * id whichever conversation is active, so these are two different code paths: calling
   * openConversation(null) unconditionally would abort and blank a conversation that was not
   * deleted, while paramId stays put — so the [paramId] effect could not restore it and
   * `/c/<still-open-id>` would render the greeting screen.
   */
  const handleDelete = useCallback(
    (id: string) => {
      const wasOpen = id === activeIdRef.current;

      if (wasOpen) openConversation(null);
      if (storage) deleteConversation(storage, id);
      refreshList();
      if (wasOpen) navigate(`/${searchRef.current}`, { replace: true });
    },
    [navigate, openConversation, refreshList, storage]
  );

  // Focus input on "/" key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === "/" &&
        !e.metaKey &&
        !e.ctrlKey &&
        document.activeElement?.tagName !== "TEXTAREA" &&
        document.activeElement?.tagName !== "INPUT"
      ) {
        e.preventDefault();
        const textarea = document.querySelector<HTMLTextAreaElement>(
          'textarea[aria-label="Chat message input"]'
        );
        textarea?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const conversationItems = useMemo(
    () =>
      conversations.map((meta) => ({
        id: meta.id,
        title: meta.title,
        href: `/c/${meta.id}${location.search}`,
        active: meta.id === paramId,
      })),
    [conversations, location.search, paramId]
  );

  const activeTitle = conversations.find((meta) => meta.id === paramId)?.title;

  return (
    <ChatAppShell
      logo={<HelpLogo />}
      logoIcon={<HelpLogoMark />}
      title={activeTitle}
      conversations={conversationItems}
      onNewChat={handleNewChat}
      onDeleteConversation={handleDelete}
      LinkComponent={ChatLink as ChatShellLinkComponent}
    >
      <ChatContainer
        messages={messages}
        input={input}
        onInputChange={setInput}
        onSend={handleSend}
        onStop={stop}
        onSuggestionSelect={startOrContinue}
        isLoading={isStreaming}
        greeting={trafficIntent?.greeting ?? <DefaultGreeting />}
        suggestionChips={trafficIntent?.suggestion_chips ?? undefined}
      />
    </ChatAppShell>
  );
}
